import { ChatMessages } from "@storytell/cp-client";
import {
  BlockRenderer as BlockRendererEnum,
  type StreamingBlock,
  type StreamingChatId,
  type StreamingOperation,
  determinePreferredRenderer,
} from "@storytell/streaming";
import { Match, Switch } from "solid-js";
import { useWire } from "~/wire";
import type { Wire } from "~/wire/definition";
import {
  ArtifactViewerRenderer,
  BlockPreview,
  ErrorRenderer,
  ImageGenerationRenderer,
  InterstitialDataRenderer,
  InterstitialRenderer,
  InterstitialUserFacingRenderer,
  RetryRenderer,
  UnknownRenderer,
  UserFacingMessage,
} from "./r";

const hasAnswerBlockInOperation = (wire: Wire, operation?: StreamingOperation): boolean => {
  if (!operation) return false;

  return operation.blocks.some((blockId) => {
    const block = wire.stream.block.get(blockId);
    return block?.messageType === ChatMessages.MessageType.CONTENT;
  });
};

export const BlockRenderer = (props: { block: StreamingBlock; chatId: StreamingChatId }) => {
  const wire = useWire();
  const operation = () => wire.stream.operation.get(props.block.operationId);
  const preferredRenderer = () => determinePreferredRenderer(props.block);
  const isRetry = () => props.block.finalContent?.type === "retry";
  const isError = () => props.block.finalContent?.type === "error";

  const isImageBlock = () => {
    const block = props.block;
    if (block.deltas && block.deltas.length > 0) {
      try {
        const firstDelta = block.deltas[0].text;
        const parsed = JSON.parse(firstDelta);
        return parsed.type === "image";
      } catch {
        return false;
      }
    }
    return false;
  };

  const shouldSkipArtifactCard = () => {
    const renderer = preferredRenderer();
    if (renderer !== BlockRendererEnum.ARTIFACT_VIEWER) return false;

    return hasAnswerBlockInOperation(wire, operation());
  };

  return (
    <Switch>
      <Match when={isRetry()}>
        <RetryRenderer block={props.block} />
      </Match>
      <Match when={isError()}>
        <ErrorRenderer block={props.block} />
      </Match>
      <Match when={isImageBlock()}>
        <ImageGenerationRenderer blockId={props.block.blockId} />
      </Match>
      <Match when={preferredRenderer() === BlockRendererEnum.INTERSTITIAL}>
        <InterstitialRenderer block={props.block} />
      </Match>
      <Match
        when={
          preferredRenderer() === BlockRendererEnum.USER_FACING_MESSAGE ||
          preferredRenderer() === BlockRendererEnum.INTERSTITIAL_CATCH
        }
      >
        <UserFacingMessage block={props.block} />
      </Match>

      <Match when={preferredRenderer() === BlockRendererEnum.INTERSTITIAL_DATA}>
        <InterstitialDataRenderer block={props.block} />
      </Match>

      <Match when={preferredRenderer() === BlockRendererEnum.INTERSTITIAL_USER_FACING}>
        <InterstitialUserFacingRenderer block={props.block} />
      </Match>

      {/* Render nothing for artifact blocks when answer exists in same operation */}
      <Match when={preferredRenderer() === BlockRendererEnum.ARTIFACT_VIEWER && shouldSkipArtifactCard()}>{null}</Match>

      <Match when={preferredRenderer() === BlockRendererEnum.ARTIFACT_VIEWER}>
        <ArtifactViewerRenderer block={props.block} />
      </Match>

      <Match when={preferredRenderer() === BlockRendererEnum.BLOCK_PREVIEW}>
        <BlockPreview block={props.block} onClick={() => {}} />
      </Match>

      <Match when={true}>
        <UnknownRenderer block={props.block} />
      </Match>
    </Switch>
  );
};
